import { DialogTrigger, Dialog, Modal } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAtom } from 'jotai';
import InputText from 'src/components/InputText/InputText';

import { updateSupportPlanStatus, updateSupportPlanDraftStatus } from '@/api/updateSupportPlans';
import Button from '@/components/Button/Button';
import { NotificationAtom } from '@/jotai/jotai';
import { SupportPlanStatusRequest, SupportPlanDraftStatusRequest } from '@/types/supportPlan';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  afterSubmit: (rejectionReason?: string) => void;
  childID: number;
  supportPlanID: number;
  lineAccessToken: string;
  status: 'approved' | 'rejected';
  target: string;
  planType: 'plan' | 'draft'; // 追加：計画か計画案かを区別
};

export const UpdateDialog = ({
  isOpen,
  setIsOpen,
  afterSubmit,
  childID,
  supportPlanID,
  lineAccessToken,
  status,
  target,
  planType,
}: Props) => {
  const [_, setNotification] = useAtom(NotificationAtom);

  // フォームの型を動的に設定
  const { register, handleSubmit } = useForm<
    SupportPlanStatusRequest | SupportPlanDraftStatusRequest
  >({});

  const onSubmit: SubmitHandler<SupportPlanStatusRequest | SupportPlanDraftStatusRequest> = async (
    data
  ) => {
    try {
      console.log('update support plan status', {
        childID,
        supportPlanID,
        lineAccessToken,
        status,
        rejection_reason: data.rejection_reason,
        planType,
      });

      // 計画案か計画かによって適切なAPIを呼び出す
      const result =
        planType === 'draft'
          ? await updateSupportPlanDraftStatus(childID, supportPlanID, lineAccessToken, {
              status: status,
              rejection_reason: data.rejection_reason,
            })
          : await updateSupportPlanStatus(childID, supportPlanID, lineAccessToken, {
              status: status,
              rejection_reason: data.rejection_reason,
            });

      console.log(result);
      // 201の場合はresultがnullになるが、成功として扱う
      if (result.status === 200 || result.status === 201) {
        console.log('APIレスポンス:', result.result);
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: `個別支援計画${planType === 'draft' ? '案' : ''}を更新しました`,
          message: `個別支援計画${planType === 'draft' ? '案' : ''}を更新しました`,
        });
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: `個別支援計画${planType === 'draft' ? '案' : ''}の更新に失敗しました`,
          message: `個別支援計画${planType === 'draft' ? '案' : ''}の更新に失敗しました`,
        });
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: `個別支援計画${planType === 'draft' ? '案' : ''}の更新に失敗しました`,
        message: `個別支援計画${planType === 'draft' ? '案' : ''}の更新に失敗しました`,
      });
    }

    setIsOpen(false);
    // 差戻しの場合は理由を渡す
    afterSubmit(status === 'rejected' ? data.rejection_reason : undefined);
  };

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Dialog className="w-[600px] rounded-lg border border-gray-400 bg-white p-6 shadow-lg outline-none">
          <div className="flex flex-row justify-between">
            <h1 className="mb-5 text-2xl font-700">{`個別支援計画${planType === 'draft' ? '案' : ''}の${status === 'approved' ? '承認' : '差戻し'}`}</h1>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-row gap-4">
              <span>{`${target}の個別支援計画${planType === 'draft' ? '案' : ''}を${status === 'approved' ? '承認' : '差戻し'}しますがよろしいですか？`}</span>
            </div>
            {status === 'rejected' && (
              <div className="mt-4">
                <InputText
                  type="text"
                  required
                  label="差戻し理由"
                  {...register('rejection_reason', { required: true })}
                />
              </div>
            )}
            <div className="mt-10 flex flex-row items-center justify-center space-x-2">
              <Button
                type="button"
                size="lg"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                戻る
              </Button>
              <Button
                variant="solid-fill"
                size="lg"
                type="submit"
              >
                {status === 'approved' ? '承認する' : '差戻しする'}
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};
